import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import bloomLogo from '../../shared/icons/bloom_bright_logo_color.svg';

// Bloom Bright brand palette (sampled from the logo).
const BRAND = {
  olive: '#8e8741',
  oliveDark: '#6f6a33',
  tan: '#daab84',
};

/**
 * Entra External ID sign-in page.
 *
 * Access is restricted to two identity providers, enforced in the External ID tenant:
 *   • Microsoft — accounts in the bloombrightllc workforce tenant
 *   • Google    — Hope's Horizon (@hopeshorizon.com) Google Workspace
 *
 * New users don't self-register: an admin creates them in the app and they receive an
 * invite email. This page shows a single "Sign in" button that hands off to External ID's
 * hosted picker, which presents the Microsoft / Google options. We don't try to deep-link a
 * specific provider via `domain_hint` — it's read as a local-account username on this tenant
 * and breaks federated sign-in. Instead, the invite email's link carries the invited user's
 * address as `?login_hint=`, which we forward as MSAL's `loginHint` — a standard OIDC hint
 * that lets Entra's Home Realm Discovery route a federated domain (e.g. @hopeshorizon.com)
 * straight to Google without showing the chooser, while leaving the chooser in place for
 * anyone who lands here without a hint (e.g. a bookmarked link or a signed-out session).
 */
function LoginPage() {
  const theme = useTheme();
  const { instance } = useMsal();
  const [pending, setPending] = useState(false);

  const signIn = () => {
    setPending(true);
    const loginHint = new URLSearchParams(window.location.search).get('login_hint');
    const request = loginHint ? { ...loginRequest, loginHint } : loginRequest;
    instance
      .loginRedirect(request)
      .catch((error) => {
        console.error('Sign-in failed:', error);
        setPending(false);
      });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        background: `radial-gradient(1000px 520px at 50% -12%, ${BRAND.tan}59, transparent), radial-gradient(900px 520px at 50% 112%, ${BRAND.olive}26, transparent), ${theme.palette.background.default}`,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 3,
          p: { xs: 3, sm: 4.5 },
          textAlign: 'center',
        }}
      >
        {/* Brand mark */}
        <Box
          component="img"
          src={bloomLogo}
          alt="Bloom Bright"
          sx={{
            width: 96,
            height: 96,
            mx: 'auto',
            mb: 2.5,
            display: 'block',
            objectFit: 'contain',
          }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5 }}>
          Sign in to continue
        </Typography>

        <Button
          fullWidth
          size="large"
          variant="contained"
          onClick={signIn}
          disabled={pending}
          startIcon={pending ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{
            py: 1.25,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 700,
            backgroundColor: BRAND.olive,
            '&:hover': { backgroundColor: BRAND.oliveDark },
          }}
        >
          {pending ? 'Redirecting…' : 'Sign in'}
        </Button>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Continue with your Microsoft or Google work account
        </Typography>

        <Divider sx={{ my: 3 }}>
          <Typography variant="caption" sx={{ px: 1, color: BRAND.oliveDark, fontWeight: 600, letterSpacing: '0.02em' }}>
            Authorized accounts only
          </Typography>
        </Divider>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
          Access is limited to Bloom Bright staff and approved partners. New users are added by
          an administrator and receive an invitation by email.
        </Typography>
      </Paper>
    </Box>
  );
}

export default LoginPage;
